// SPDX-License-Identifier: LicenseRef-Proprietary

import { useMemo, useRef, useCallback } from 'react';
import {
  LegacyDecisionTree,
  LegacyLayoutNode,
  layoutTreeLayered,
  findNode,
} from '../../../lib/tree';

interface SizeConfig {
  width: number;
  height: number;
  fontSize: number;
  hGap: number;
  vGap: number;
}

interface UseTreeLayoutParams {
  tree: LegacyDecisionTree;
  structureVersion: string;
  nodeSize: 'S' | 'M' | 'L';
}

export function useTreeLayout({ tree, structureVersion, nodeSize }: UseTreeLayoutParams) {
  const sizeConfig = useMemo<SizeConfig>(() => {
    switch (nodeSize) {
      // Reduced vGap by ~12% to tighten vertical spacing between levels
      case 'S': return { width: 80,  height: 40, fontSize: 10, hGap: 48,  vGap: 96 };
      case 'M': return { width: 120, height: 60, fontSize: 12, hGap: 60,  vGap: 122 };
      case 'L': return { width: 160, height: 80, fontSize: 14, hGap: 80,  vGap: 150 };
      default:  return { width: 120, height: 60, fontSize: 12, hGap: 60,  vGap: 122 };
    }
  }, [nodeSize]);

  const layoutNodes = useMemo<LegacyLayoutNode[]>(
    () => layoutTreeLayered(tree.root, {
      hGap: sizeConfig.hGap,
      vGap: sizeConfig.vGap,
      nodeWidth: sizeConfig.width,
      nodeHeight: sizeConfig.height
    }),
    [tree.root, sizeConfig]
  );

  const dataCache = useMemo(
    () => new Map<string, ReturnType<typeof findNode>>(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [structureVersion]
  );
  const getNodeData = useCallback((id: string) => {
    if (!dataCache.has(id)) dataCache.set(id, findNode(tree, id));
    return dataCache.get(id)!;
  }, [tree, dataCache]);

  const getSVGDimensions = useCallback(() => {
    if (layoutNodes.length === 0) {
      return { width: 400, height: 300 };
    }
    
    const maxX = Math.max(0, ...layoutNodes.map(n => n.x + sizeConfig.width));
    const maxY = Math.max(0, ...layoutNodes.map(n => n.y + sizeConfig.height));
    
    return {
      width: Math.max(400, maxX + 50),
      height: Math.max(300, maxY + 100)
    };
  }, [layoutNodes, sizeConfig]);

  // Freeze dimensions per structureVersion (not selection)
  const dimsRef = useRef<{ width: number; height: number; key: string } | null>(null);
  if (!dimsRef.current || dimsRef.current.key !== structureVersion) {
    const d = getSVGDimensions();
    dimsRef.current = { ...d, key: structureVersion };
  }
  const dimensions = dimsRef.current;

  return {
    layoutNodes,
    sizeConfig,
    dimensions,
    getNodeData,
  };
}

